import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowLeft, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchSystemCaregivers,
  buildSystemCaregiverCacheKey,
  hydrateSystemCaregiverList,
  deleteSystemCaregiver,
  setSorting
} from '../../../store/slices/systemCaregiverSlice';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import SharedDetailModal from '../../../components/Common/SharedDetailModal/SharedDetailModal';
import DetailsPage from '../../../components/Common/DetailsPage/DetailsPage';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import Heading from '../../../components/Common/Heading/Heading';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import SystemCaregiverPatientList from './SystemCaregiverPatientList';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import '../AdminManagement/AdminManagement.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './SystemCaregiverManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';

const SystemCaregiverManagement = ({ practiceId, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearBreadcrumbs } = useBreadcrumb();
  const {
    systemCaregivers,
    systemCaregiverFetchedKeys,
    listFetchLoading,
    error,
    pagination,
    sortBy,
    sortOrder
  } = useSelector((state) => state.systemCaregiver);
  const { user } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filteredSystemCaregivers, setFilteredSystemCaregivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('30');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState([]);

  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');

  useEffect(() => {
    if (!practiceId) {
      clearBreadcrumbs();
    }
  }, [clearBreadcrumbs, practiceId]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [localSearchTerm, searchTerm]);

  const listParams = useMemo(
    () => ({
      page: currentPage,
      limit: entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10),
      search: searchTerm,
      practiceId: practiceId || undefined,
      sortBy,
      sortOrder
    }),
    [currentPage, entriesPerPage, searchTerm, practiceId, sortBy, sortOrder]
  );

  const cacheKey = useMemo(() => buildSystemCaregiverCacheKey(listParams), [listParams]);

  useEffect(() => {
    if (!location.state?.refreshSystemCaregiverList) return;
    dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    navigate(
      {
        pathname: location.pathname,
        search: location.search
      },
      { replace: true, state: {} }
    );
  }, [dispatch, listParams, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (systemCaregiverFetchedKeys[cacheKey]) {
      dispatch(hydrateSystemCaregiverList(cacheKey));
    } else {
      dispatch(fetchSystemCaregivers(listParams));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listParams encoded in cacheKey
  }, [cacheKey, dispatch]);

  useEffect(() => {
    if (systemCaregivers && systemCaregivers.length > 0) {
      let filtered = systemCaregivers.filter((c) => !c.deleted_at);
      if (localSearchTerm.trim()) {
        const qParts = localSearchTerm.toLowerCase().trim().split(/\s+/);
        filtered = filtered.filter((caregiver) => {
          const fullName = getDisplayName(caregiver).toLowerCase();
          const email = (caregiver.email || '').toLowerCase();
          const phone = (caregiver.phone || '').toLowerCase();

          return qParts.every(part =>
            fullName.includes(part) ||
            email.includes(part) ||
            phone.includes(part)
          );
        });
      }
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'idx') {
          const ai = Number(a.id) || 0;
          const bi = Number(b.id) || 0;
          return sortOrder === 'asc' ? ai - bi : bi - ai;
        }
        if (sortBy === 'status') {
          // Status order: Active (1) > Inactive (2)
          const o = { active: 1, inactive: 2 };
          const sa = (a.status || '').toLowerCase();
          const sb = (b.status || '').toLowerCase();
          const orderA = o[sa] || (sa === '' ? 3 : 4);
          const orderB = o[sb] || (sb === '' ? 3 : 4);

          if (orderA !== orderB) {
            return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
          }
          // Tie-breaker: Name A-Z (always asc unless status sort itself is desc, but usually we want names A-Z)
          const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          return na.localeCompare(nb);
        }

        if (sortBy === 'name' || sortBy === 'first_name') {
          if (sortBy === 'addr') {
            const va = String(a.address?.address_line_1 || a.address_line_1 || '').toLowerCase();
            const vb = String(b.address?.address_line_1 || b.address_line_1 || '').toLowerCase();
            return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          }
          if (sortBy === 'phone') {
            const va = String(a.phone || '').toLowerCase();
            const vb = String(b.phone || '').toLowerCase();
            return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          }
          if (sortBy === 'cell') {
            const va = String(a.cell_phone || '').toLowerCase();
            const vb = String(b.cell_phone || '').toLowerCase();
            return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          }
          if (sortBy === 'fax') {
            const va = String(a.fax || '').toLowerCase();
            const vb = String(b.fax || '').toLowerCase();
            return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          }
          if (sortBy === 'email') {
            const va = String(a.email || '').toLowerCase();
            const vb = String(b.email || '').toLowerCase();
            return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          }
          const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          return sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
        }

        const va = a[sortBy] || '';
        const vb = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(va).localeCompare(String(vb), undefined, { numeric: true })
          : String(vb).localeCompare(String(va), undefined, { numeric: true });
      });
      // Remove optimistic deletes
      filtered = filtered.filter(c => !optimisticDeletedIds.includes(c.id));

      setFilteredSystemCaregivers(filtered);
    } else {
      setFilteredSystemCaregivers([]);
    }
  }, [systemCaregivers, sortBy, sortOrder, localSearchTerm, optimisticDeletedIds]);

  const totalPages = pagination.total_pages || 1;
  const currentSystemCaregivers = filteredSystemCaregivers;

  const scStatus = (s) => {
    const v = (s || 'active').toString().toLowerCase();
    return v === 'active' ? 'Active' : 'Inactive';
  };

  const getDisplayName = (caregiver) =>
    `${caregiver.last_name || ''}, ${caregiver.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() ||
    caregiver.name ||
    'N/A';

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const formatAddressLines = (caregiver) =>
    buildDetailAddressLines({
      line1: caregiver.address?.address_line_1 ?? caregiver.address_line_1,
      line2: caregiver.address?.address_line_2 ?? caregiver.address_line_2,
      city: caregiver.address?.city ?? caregiver.city,
      stateDisplay: resolveStateDisplay(caregiver),
      zip: caregiver.address?.zip_code ?? caregiver.zip_code
    });


  const getUsername = (entity) =>
    entity?.username || entity?.user?.username || entity?.login_username || 'N/A';

  const getTwoWayAuth = (entity) => {
    const status =
      entity?.is_two_way_auth ??
      entity?.two_way_authentication_enabled ??
      entity?.twoWayAuth ??
      entity?.two_way_auth ??
      entity?.two_way_authentication ??
      entity?.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const handleViewSystemCaregiver = (caregiver) => {
    const params = new URLSearchParams(window.location.search);
    params.set('viewId', caregiver.id);
    navigate({ search: params.toString() });
  };

  const closeSystemCaregiverDetails = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() });
  };

  const handleEditSystemCaregiver = (caregiverId) => {
    navigate(`/system-caregiver-dashboard/add-system-caregiver?editId=${caregiverId}`);
  };

  const handleDeleteSystemCaregiver = (caregiver) => {
    setCaregiverToDelete(caregiver);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!caregiverToDelete) return;
    const idToDelete = caregiverToDelete.id;
    try {
      // Optimistic UI: Hide from list and close modal immediately
      setOptimisticDeletedIds(prev => [...prev, idToDelete]);
      setShowDeleteModal(false);
      setCaregiverToDelete(null);
      toast.success('System Caregiver deleted successfully');

      await dispatch(deleteSystemCaregiver(idToDelete)).unwrap();

      // Refresh list to stay in sync
      dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    } catch (err) {
      // Rollback on error
      setOptimisticDeletedIds(prev => prev.filter(id => id !== idToDelete));

      console.error('Failed to delete system caregiver:', err);
      toast.error('Failed to delete system caregiver');
      dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCaregiverToDelete(null);
  };

  const handleAddSystemCaregiver = () => {
    navigate('/system-caregiver-dashboard/add-system-caregiver');
  };

  const handleSort = useCallback(
    (key) => {
      const newOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
      dispatch(setSorting({ sortBy: key, sortOrder: newOrder }));
      setCurrentPage(1);
    },
    [dispatch, sortBy, sortOrder]
  );

  const startIndex = 0;
  const columns = useMemo(
    () => [
      {
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{startIndex + i + 1}</span>
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={scStatus(row.status)} />
      },
      {
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="admin-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewSystemCaregiver(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      },
      {
        key: 'addr',
        title: 'Address',
        sortable: true,
        render: (row) => formatAddressLines(row).join(', ')
      },
      { key: 'phone', title: 'Phone', sortable: true, render: (row) => getSafeValue(row.phone) },
      { key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => getSafeValue(row.cell_phone) },
      { key: 'fax', title: 'Fax', sortable: true, render: (row) => getSafeValue(row.fax) },
      { key: 'email', title: 'Email', sortable: true, render: (row) => getSafeValue(row.email) },
      {
        key: 'actions',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewSystemCaregiver(row)}
            onEdit={!isReadOnly ? () => handleEditSystemCaregiver(row.id) : null}
            onDelete={!isReadOnly ? () => handleDeleteSystemCaregiver(row) : null}
          />
        )
      }
    ],
    [isReadOnly]
  );

  const showLoading = listFetchLoading && !systemCaregiverFetchedKeys[cacheKey];
  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);

  const caregiverDetails = useMemo(() => {
    if (!viewId) return null;
    return systemCaregivers?.find(c => String(c.id) === String(viewId));
  }, [viewId, systemCaregivers]);

  const listFetchedForKey = Boolean(systemCaregiverFetchedKeys[cacheKey]);
  const staleDetailViewId =
    Boolean(viewId) &&
    listFetchedForKey &&
    !showLoading &&
    !listFetchLoading &&
    caregiverDetails === undefined;

  useEffect(() => {
    if (!staleDetailViewId) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() }, { replace: true });
  }, [staleDetailViewId, navigate]);

  if (viewId && !staleDetailViewId) {
    return (
      <div className="scm-details-container">
        <div className="list-screen-top-bar scm-details-top-bar">
          <div className="list-screen-title-section">
            <Heading title="System Caregiver Details" level="h2" className="list-screen-title-text" />
          </div>
        </div>
        <div className="scm-details-header">
          <button className="scm-back-btn" onClick={closeSystemCaregiverDetails}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
          </button>
        </div>

        <DetailsPage
          title="System Caregiver"
          data={caregiverDetails}
          isLoading={showLoading}
          config={[
            {
              placement: 'left',
              fields: [
                { label: 'Name', value: (data) => getDisplayName(data) },
                { label: 'Address', value: (data) => formatAddressLines(data), multiline: true },
                { label: 'Email', value: (data) => getSafeValue(data.email) },
                { label: 'Home Phone', value: (data) => getSafeValue(data.phone) },
                { label: 'Cell Phone', value: (data) => getSafeValue(data.cell_phone) },
                { label: 'Fax', value: (data) => getSafeValue(data.fax) },
                {
                  label: 'Status',
                  value: (data) =>
                    (data.status || 'Active').charAt(0).toUpperCase() +
                    (data.status || 'Active').slice(1).toLowerCase(),
                  isBadge: true
                }
              ]
            },
            {
              placement: 'right',
              fields: [
                { label: 'Username', value: (data) => getUsername(data) },
                { label: 'Two Way Authentication', value: (data) => getTwoWayAuth(data) }
              ]
            }
          ]}
        />

        {!isReadOnly && <SystemCaregiverPatientList caregiverId={viewId} />}
      </div>
    );
  }

  return (
    <div className="admin-management-container">
      {error && !showLoading && (
        <div className="admin-management-alert admin-management-alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <div className="admin-management-header-row">
        <h2 className="page-title">System Caregiver List</h2>

        <div className="admin-management-header-actions">
          <SearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            className="admin-management-search-wrapper"
          />
          {user?.role_id === 1 && !practiceId && !isReadOnly && (
            <button type="button" className="list-screen-add-btn" onClick={handleAddSystemCaregiver}>
              <FontAwesomeIcon icon={faPlus} className="admin-management-add-icon" />
              Add System Caregiver
            </button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={showLoading ? [] : currentSystemCaregivers}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showLoading}
        loadingMode="spinner"
        emptyMessage="No system caregivers found"
      />

      {/* Pagination */}
      <div className="list-screen-pagination-section">
        <div className="list-screen-pagination-left">
          <div className="list-screen-entries-selector">
            <span>Show</span>
            <select
              className="list-screen-entries-select"
              style={{ width: '80px' }}
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="all">All</option>
            </select>
            <span>entries</span>
          </div>
          {!practiceId && (
            <button
              type="button"
              className="list-screen-option-btn"
              onClick={() => navigate('/event-logs/System%20Caregiver/all')}
            >
              <FontAwesomeIcon icon={faList} className="admin-management-add-icon" />
              Events Log
            </button>
          )}
        </div>

        <div className="list-screen-pagination-right">
          <div className="list-screen-page-selector">
            <span>Page</span>
            <select
              className="list-screen-page-select"
              style={{ width: '60px' }}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <option key={page} value={page}>
                  {page}
                </option>
              ))}
            </select>
            <span>of {totalPages}</span>
          </div>
        </div>
      </div>
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDelete}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the system caregiver{' '}
                <strong>{getDisplayName(caregiverToDelete)}</strong>?
              </p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default SystemCaregiverManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="scm-detail-row">
    <span className="scm-detail-label">{label}</span>
    <span className="scm-detail-separator">:</span>
    <span className={`scm-detail-value ${multiline ? 'scm-detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);
